import BrowserPhoneEventBus from './eventBus';
import {
  buildBrowserPhoneOptions,
  getBrowserPhoneRuntimeUrl,
  getBrowserPhoneScriptUrls,
  getBrowserPhoneStyleUrls,
} from './config';
import {
  normalizeLegacySession,
  serializeLegacyError,
} from './sessionManager';

const RESOURCE_ATTR = 'data-browser-phone-resource';

const waitFor = (predicate, timeout = 15000) =>
  new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      if (predicate()) {
        window.clearInterval(interval);
        resolve();
        return;
      }

      if (Date.now() - startedAt >= timeout) {
        window.clearInterval(interval);
        reject(new Error('Browser-Phone runtime timed out'));
      }
    }, 100);
  });

const loadStyleOnce = href =>
  new Promise((resolve, reject) => {
    const selector = `link[${RESOURCE_ATTR}="${href}"]`;
    const existing = document.querySelector(selector);

    if (existing) {
      resolve(existing);
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute(RESOURCE_ATTR, href);
    link.onload = () => resolve(link);
    link.onerror = () =>
      reject(new Error(`Failed to load Browser-Phone style: ${href}`));
    document.head.appendChild(link);
  });

const loadScriptOnce = src =>
  new Promise((resolve, reject) => {
    const selector = `script[${RESOURCE_ATTR}="${src}"]`;
    const existing = document.querySelector(selector);

    if (existing?.dataset.loaded === 'true') {
      resolve(existing);
      return;
    }

    if (existing) {
      existing.addEventListener('load', () => resolve(existing), {
        once: true,
      });
      existing.addEventListener(
        'error',
        () =>
          reject(new Error(`Failed to load Browser-Phone script: ${src}`)),
        { once: true }
      );
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.defer = false;
    script.setAttribute(RESOURCE_ATTR, src);
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve(script);
    };
    script.onerror = () =>
      reject(new Error(`Failed to load Browser-Phone script: ${src}`));
    document.body.appendChild(script);
  });

const getLineElement = (line, suffix) => {
  if (!line) return null;

  return document.getElementById(`line-${line}-${suffix}`);
};

const isElementVisible = element => {
  if (!element) return false;

  const styles = window.getComputedStyle(element);
  return styles.display !== 'none' && styles.visibility !== 'hidden';
};

const readElementText = element => {
  const text = element?.textContent?.trim();
  return text || null;
};

const isEstablishedSession = session => {
  if (!session) return false;

  if (session.data?.started) return true;

  const establishedState = window.SIP?.SessionState?.Established;
  if (establishedState && session.state === establishedState) {
    return true;
  }

  return false;
};

class BrowserPhoneEngine {
  constructor() {
    this.bootstrap = null;
    this.runtimeOptions = null;
    this.eventBus = new BrowserPhoneEventBus();
    this.initializationPromise = null;
    this.root = null;
    this.runtimeLoaded = false;
    this.globalHooksBound = false;
  }

  on(eventName, handler) {
    return this.eventBus.on(eventName, handler);
  }

  emit(eventName, payload) {
    this.eventBus.emit(eventName, payload);
  }

  getLegacySession(line) {
    if (!line || typeof window.FindLineByNumber !== 'function') {
      return null;
    }

    return window.FindLineByNumber(line)?.SipSession || null;
  }

  emitLineState(line, overrides = {}) {
    const session = this.getLegacySession(line);
    if (!session) return;

    this.emit('call:updated', normalizeLegacySession(session, overrides));
  }

  attachRoot(element) {
    if (!element) return;

    this.root = element;
    this.root.id = 'Phone';
  }

  isLoaded() {
    return this.runtimeLoaded;
  }

  setBootstrapOptions(bootstrap) {
    this.bootstrap = bootstrap;
    this.runtimeOptions = buildBrowserPhoneOptions(bootstrap);
    window.__browserPhoneConsumeOptions = () => {
      const options = this.runtimeOptions;
      this.runtimeOptions = null;
      return options ? { ...options } : {};
    };
  }

  async initialize(bootstrap) {
    this.bootstrap = bootstrap;

    if (!this.root) {
      throw new Error('Browser-Phone runtime root is not attached');
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.loadRuntime(bootstrap).catch(error => {
      this.initializationPromise = null;
      throw error;
    });

    return this.initializationPromise;
  }

  async loadRuntime(bootstrap) {
    this.bindGlobalHooks();

    const styleUrls = getBrowserPhoneStyleUrls(bootstrap.hosting_prefix);
    const scriptUrls = getBrowserPhoneScriptUrls();

    await Promise.all(styleUrls.map(loadStyleOnce));

    for (const scriptUrl of scriptUrls) {
      // eslint-disable-next-line no-await-in-loop
      await loadScriptOnce(scriptUrl);
    }

    this.setBootstrapOptions(bootstrap);
    await loadScriptOnce(getBrowserPhoneRuntimeUrl(bootstrap.hosting_prefix));
    await waitFor(() => typeof window.DialByLine === 'function');
    this.clearPhoneOptions();
    this.runtimeLoaded = true;
  }

  bindGlobalHooks() {
    if (this.globalHooksBound) return;

    this.globalHooksBound = true;

    window.ShowPhone = () => {
      this.emit('widget:show');
    };

    window.browserPhoneHooks = {
      nativeMode: true,
      showWidget: () => this.emit('widget:show'),
      cdrCreated: cdr => this.emit('cdr:created', cdr),
    };

    this.bindHook('web_hook_on_init', () => {
      this.emit('runtime:initialized');
    });
    this.bindHook('web_hook_on_register', userAgent => {
      this.emit('registration:changed', {
        status: 'registered',
        userAgent,
      });
    });
    this.bindHook('web_hook_on_unregistered', () => {
      this.emit('registration:changed', {
        status: 'unregistered',
      });
    });
    this.bindHook('web_hook_on_registrationFailed', error => {
      this.emit('registration:changed', {
        status: 'failed',
        error: serializeLegacyError(error),
      });
    });
    this.bindHook('web_hook_on_transportError', () => {
      this.emit('registration:changed', {
        status: 'transport_error',
      });
    });
    this.bindHook('web_hook_on_invite', session => {
      this.emit('call:updated', normalizeLegacySession(session));
    });
    this.bindHook('web_hook_on_modify', (action, session) => {
      this.emit(
        'call:updated',
        normalizeLegacySession(session, {
          lastAction: action,
        })
      );
    });
    this.bindHook('web_hook_on_terminate', session => {
      this.emit(
        'call:ended',
        normalizeLegacySession(session, {
          status: 'ended',
        })
      );
    });
  }

  bindHook(name, handler) {
    const previousHandler = window[name];

    window[name] = (...args) => {
      handler(...args);
      if (typeof previousHandler === 'function') {
        previousHandler(...args);
      }
    };
  }

  showWidget() {
    this.emit('widget:show');
  }

  dial(number) {
    this.showWidget();
    window.DialByLine?.('audio', null, number);
  }

  answer(line) {
    this.showWidget();
    window.AnswerAudioCall?.(line);
  }

  register() {
    window.Register?.();
  }

  unregister() {
    window.Unregister?.(true);
  }

  clearPhoneOptions() {
    this.runtimeOptions = null;
    delete window.phoneOptions;
    delete window.__browserPhoneConsumeOptions;
  }

  clearTransientUi() {
    window.CloseWindow?.(true);
  }

  reject(line) {
    window.RejectCall?.(line);
  }

  hold(line) {
    window.holdSession?.(line);
  }

  resume(line) {
    window.unholdSession?.(line);
  }

  mute(line) {
    window.MuteSession?.(line);
  }

  unmute(line) {
    window.UnmuteSession?.(line);
  }

  hangup(line) {
    const session = this.getLegacySession(line);

    if (!isEstablishedSession(session)) {
      window.cancelSession?.(line);
      return;
    }

    window.endSession?.(line);
  }

  sendDtmf(line, tone) {
    window.sendDTMF?.(line, tone);
  }

  async getRecordingBlob(recordingUid, { attempts = 12, interval = 250 } = {}) {
    if (!recordingUid || typeof window.GetCallRecordingBlob !== 'function') {
      return null;
    }

    for (let attempt = 0; attempt < attempts; attempt += 1) {
      try {
        const blob = await window.GetCallRecordingBlob(recordingUid);
        if (blob?.size) {
          return blob;
        }
      } catch {
        // Ignore transient IndexedDB reads while the recording is still being flushed.
      }

      if (attempt < attempts - 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise(resolve => {
          window.setTimeout(resolve, interval);
        });
      }
    }

    return null;
  }

  startRecording(line) {
    window.StartRecording?.(line);
    window.setTimeout(() => {
      this.emitLineState(line, { lastAction: 'recording_started' });
    }, 50);
  }

  stopRecording(line) {
    window.StopRecording?.(line, true);
    window.setTimeout(() => {
      this.emitLineState(line, { lastAction: 'recording_stopped' });
    }, 50);
  }

  setLineInputValue(line, suffix, value) {
    const input = getLineElement(line, suffix);
    if (!input) return false;

    input.value = value;
    return true;
  }

  clickLineButton(line, suffix) {
    const button = getLineElement(line, suffix);
    if (!button) return false;

    button.click();
    return true;
  }

  getTransferState(line) {
    if (!line) {
      return {
        isActive: false,
        canBlindTransfer: false,
        canAttendedTransfer: false,
        canCompleteAttendedTransfer: false,
        canCancelAttendedTransfer: false,
        canTerminateAttendedTransfer: false,
        statusText: null,
      };
    }

    return {
      isActive: isElementVisible(getLineElement(line, 'btn-CancelTransfer')),
      canBlindTransfer: isElementVisible(
        getLineElement(line, 'btn-blind-transfer')
      ),
      canAttendedTransfer: isElementVisible(
        getLineElement(line, 'btn-attended-transfer')
      ),
      canCompleteAttendedTransfer: isElementVisible(
        getLineElement(line, 'btn-complete-attended-transfer')
      ),
      canCancelAttendedTransfer: isElementVisible(
        getLineElement(line, 'btn-cancel-attended-transfer')
      ),
      canTerminateAttendedTransfer: isElementVisible(
        getLineElement(line, 'btn-terminate-attended-transfer')
      ),
      statusText: readElementText(getLineElement(line, 'transfer-status')),
    };
  }

  getConferenceState(line) {
    if (!line) {
      return {
        isActive: false,
        canDial: false,
        canCancelDial: false,
        canJoin: false,
        canTerminate: false,
        statusText: null,
      };
    }

    return {
      isActive: isElementVisible(getLineElement(line, 'btn-CancelConference')),
      canDial: isElementVisible(getLineElement(line, 'btn-conference-dial')),
      canCancelDial: isElementVisible(
        getLineElement(line, 'btn-cancel-conference-dial')
      ),
      canJoin: isElementVisible(getLineElement(line, 'btn-join-conference-call')),
      canTerminate: isElementVisible(
        getLineElement(line, 'btn-terminate-conference-call')
      ),
      statusText: readElementText(getLineElement(line, 'conference-status')),
    };
  }

  ensureTransferSession(line) {
    const state = this.getTransferState(line);
    if (state.isActive) return true;

    window.StartTransferSession?.(line);
    return this.getTransferState(line).isActive;
  }

  ensureConferenceSession(line) {
    const state = this.getConferenceState(line);
    if (state.isActive) return true;

    window.StartConferenceCall?.(line);
    return this.getConferenceState(line).isActive;
  }

  blindTransfer(line, number) {
    if (!line || !number || !this.ensureTransferSession(line)) return false;
    if (!this.setLineInputValue(line, 'txt-FindTransferBuddy', number)) {
      return false;
    }

    // Same asynchronous-hold race as the attended transfer: BlindTransfer()
    // bails out until the call is on hold. Retry until the hold settles so a
    // single click is enough.
    waitFor(() => window.BlindTransfer?.(line) !== false, 5000).catch(() => {});

    return true;
  }

  startAttendedTransfer(line, number) {
    if (!line || !number || !this.ensureTransferSession(line)) return false;
    if (!this.setLineInputValue(line, 'txt-FindTransferBuddy', number)) {
      return false;
    }

    // Starting the transfer session holds the active call asynchronously, and
    // AttendedTransfer() bails out (returning false) until that hold is
    // confirmed. Retry until the hold settles so the consultation call is
    // placed on the first click instead of requiring a second one.
    waitFor(() => window.AttendedTransfer?.(line) !== false, 5000).catch(
      () => {}
    );

    return true;
  }

  completeAttendedTransfer(line) {
    return this.clickLineButton(line, 'btn-complete-attended-transfer');
  }

  cancelTransfer(line) {
    const cancelled = this.clickLineButton(
      line,
      'btn-cancel-attended-transfer'
    );

    if (cancelled) return true;

    window.CancelTransferSession?.(line);
    return true;
  }

  endAttendedTransfer(line) {
    const terminated = this.clickLineButton(
      line,
      'btn-terminate-attended-transfer'
    );

    if (terminated) return true;

    window.CancelTransferSession?.(line);
    return true;
  }

  startConference(line, number) {
    if (!line || !number || !this.ensureConferenceSession(line)) return false;
    if (!this.setLineInputValue(line, 'txt-FindConferenceBuddy', number)) {
      return false;
    }

    return window.ConferenceDial?.(line) !== false;
  }

  joinConference(line) {
    return this.clickLineButton(line, 'btn-join-conference-call');
  }

  cancelConference(line) {
    const cancelled = this.clickLineButton(line, 'btn-cancel-conference-dial');

    if (cancelled) return true;

    window.CancelConference?.(line);
    return true;
  }

  endConference(line) {
    const terminated = this.clickLineButton(
      line,
      'btn-terminate-conference-call'
    );

    if (terminated) return true;

    window.CancelConference?.(line);
    return true;
  }
}

export const browserPhoneEngine = new BrowserPhoneEngine();
